/**
 * Project phases - the stages of a project's life that budget and actuals are split across.
 * Mirrors backend/constants/project_phases.py.
 *
 * Splitting the numbers this way is what makes performance readable: Development & Renovation
 * spend can be judged against the appreciation or sale it produced, and Operation spend against
 * the rental NOI it produced. Not every strategy has every phase.
 */
import { Key, Hammer, House, Handshake, Question } from '@phosphor-icons/react';
import { normalizeProjectType } from './projectTypes';

export const PROJECT_PHASES = [
  {
    value: 'acquisition',
    label: 'Acquisition',
    shortLabel: 'Acquisition',
    description: 'Purchase price, taxes and closing costs',
    icon: Key,
    accent: 'text-sky-600',
    chip: 'bg-sky-100 text-sky-700',
    dot: 'bg-sky-500',
    bar: 'bg-sky-500',
  },
  {
    value: 'development',
    label: 'Development & Renovation',
    shortLabel: 'Development',
    description: 'Construction, refurbishment, permits and fit-out',
    icon: Hammer,
    accent: 'text-amber-600',
    chip: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-500',
    bar: 'bg-amber-500',
  },
  {
    value: 'operation',
    label: 'Operation',
    shortLabel: 'Operation',
    description: 'Rental income and the costs of running the property',
    icon: House,
    accent: 'text-emerald-600',
    chip: 'bg-emerald-100 text-emerald-700',
    dot: 'bg-emerald-500',
    bar: 'bg-emerald-500',
  },
  {
    value: 'exit',
    label: 'Exit',
    shortLabel: 'Exit',
    description: 'Sale proceeds and the costs of disposing of the property',
    icon: Handshake,
    accent: 'text-violet-600',
    chip: 'bg-violet-100 text-violet-700',
    dot: 'bg-violet-500',
    bar: 'bg-violet-500',
  },
];

export const UNASSIGNED_PHASE = {
  value: 'unassigned',
  label: 'Unassigned',
  shortLabel: 'Unassigned',
  description: 'Not yet allocated to a phase',
  icon: Question,
  accent: 'text-slate-500',
  chip: 'bg-slate-100 text-slate-600',
  dot: 'bg-slate-400',
  bar: 'bg-slate-400',
};

export const PHASE_VALUES = PROJECT_PHASES.map((p) => p.value);

/** Which phases each strategy actually goes through. */
export const PHASES_BY_PROJECT_TYPE = {
  renovate_sell: ['acquisition', 'development', 'exit'],
  new_development_sale: ['acquisition', 'development', 'exit'],
  renovate_rent: ['acquisition', 'development', 'operation'],
  student_housing_rental: ['acquisition', 'development', 'operation'],
  residential_rental: ['acquisition', 'operation'],
  hybrid: ['acquisition', 'development', 'operation', 'exit'],
  other: [...PHASE_VALUES],
};

/** The same four phases, named the way each strategy talks about them. */
export const PHASE_LABEL_OVERRIDES = {
  renovate_sell: { development: 'Renovation', exit: 'Sale & Exit' },
  renovate_rent: { development: 'Renovation', operation: 'Rental Operation' },
  hybrid: { development: 'Renovation', operation: 'Rental Operation', exit: 'Sale & Exit' },
  student_housing_rental: { development: 'Development & Fit-out', operation: 'Operations & Rental' },
  residential_rental: { operation: 'Rental Operation' },
  new_development_sale: {
    acquisition: 'Land & Acquisition',
    development: 'Construction',
    exit: 'Sales & Handover',
  },
};

const LEGACY_PHASES = {
  renovation: 'development',
  development_renovation: 'development',
  construction: 'development',
  works: 'development',
  operations: 'operation',
  operating: 'operation',
  rental: 'operation',
  hold: 'operation',
  sale: 'exit',
  disposal: 'exit',
  purchase: 'acquisition',
};

const ACCOUNT_PHASES = {
  1200: 'acquisition',
  1210: 'development',
  1300: 'acquisition',
  1400: 'operation',
  2300: 'operation',
  4000: 'operation',
  4100: 'operation',
  4200: 'exit',
  4300: 'exit',
  4400: 'operation',
  4500: 'operation',
  8000: 'operation',
  8100: 'operation',
  8200: 'exit',
  8300: 'exit',
  8400: 'exit',
};

const ACCOUNT_BLOCK_PHASES = { 5: 'acquisition', 6: 'development', 7: 'operation' };

export const normalizePhase = (value) => {
  if (!value) return null;
  const canonical = LEGACY_PHASES[value] || value;
  return PHASE_VALUES.includes(canonical) ? canonical : null;
};

export const phaseMeta = (value) => {
  const canonical = normalizePhase(value);
  return PROJECT_PHASES.find((p) => p.value === canonical) || UNASSIGNED_PHASE;
};

export const phaseLabel = (value, projectType) => {
  const canonical = normalizePhase(value);
  if (!canonical) return UNASSIGNED_PHASE.label;
  const override = PHASE_LABEL_OVERRIDES[normalizeProjectType(projectType)]?.[canonical];
  return override || phaseMeta(canonical).label;
};

export const phasesForProjectType = (projectType) =>
  PHASES_BY_PROJECT_TYPE[normalizeProjectType(projectType)] || [...PHASE_VALUES];

export const inferPhaseFromAccount = (accountCode) => {
  if (!accountCode) return null;
  const code = String(accountCode).trim();
  return ACCOUNT_PHASES[code] || ACCOUNT_BLOCK_PHASES[code[0]] || null;
};

/** Phase list for pickers, flagged by whether the strategy goes through them. */
export const phaseOptions = (projectType) => {
  const applicable = projectType ? phasesForProjectType(projectType) : PHASE_VALUES;
  return PROJECT_PHASES.map((p) => ({
    ...p,
    label: phaseLabel(p.value, projectType),
    shortLabel: PHASE_LABEL_OVERRIDES[normalizeProjectType(projectType)]?.[p.value] || p.shortLabel,
    applicable: applicable.includes(p.value),
  }));
};

export default PROJECT_PHASES;
